import * as React from 'react';
import { useState } from 'react';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  TouchableOpacity,
  Alert,
  ActivityIndicator,
  Platform
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import * as DocumentPicker from 'expo-document-picker';
import theme from '../../theme';
import { 
  importCareemPaymentData,
  importUberCaptainData,
  importTrafficFinesData,
  importCreditTermsData,
  processSalikCSV,
  getImportBatchSummary,
  recalculateAllDriverBalances,
  supabase
} from '../../api/supabase';
import { importVerificationService } from '../../services/importVerificationService';
import { ImportVerificationModal } from '../../components/modals/ImportVerificationModal';
import { VerificationResult, ImportType } from '../../types/importVerification';

interface CSVImportScreenProps {
  navigation: any;
}

interface ImportProgress {
  phase: 'idle' | 'uploading' | 'processing' | 'complete' | 'error';
  currentStep: number;
  totalSteps: number;
  message: string;
  details?: string;
  processed?: number;
  total?: number;
}

export const CSVImportScreen: React.FC<CSVImportScreenProps> = ({ navigation }) => {
  const [activeImportType, setActiveImportType] = useState<'careem' | 'uber' | 'fines' | 'salik'>('careem');
  const [processing, setProcessing] = useState(false);
  const [progress, setProgress] = useState<ImportProgress>({
    phase: 'idle',
    currentStep: 0,
    totalSteps: 6,
    message: 'Ready to import data',
  });
  const [results, setResults] = useState<{
    processed: number;
    success: number;
    failed: number;
    errors: string[];
  }>({
    processed: 0,
    success: 0,
    failed: 0,
    errors: []
  });
  
  // Verification system state
  const [verificationModalVisible, setVerificationModalVisible] = useState(false);
  const [verificationResult, setVerificationResult] = useState<VerificationResult | null>(null);
  const [importHistoryId, setImportHistoryId] = useState<string | null>(null);
  const [currentFileName, setCurrentFileName] = useState<string>('');

  // Import process steps
  const steps = [
    { id: 1, name: 'File Upload', description: 'Upload and validate CSV file' },
    { id: 2, name: 'Duplicate Check', description: 'Check for duplicate data' },
    { id: 3, name: 'Data Parsing', description: 'Parse and validate CSV structure' },
    { id: 4, name: 'Driver Matching', description: 'Match records with existing drivers' },
    { id: 5, name: 'Data Processing', description: 'Process transactions and balances' },
    { id: 6, name: 'Verification', description: 'Verify import results' },
  ];

  // Enhanced import function with verification and progress tracking
  const performVerifiedImport = async (importType: ImportType, csvText: string, fileName: string) => {
    try {
      setProgress({ 
        phase: 'processing', 
        currentStep: 3, 
        totalSteps: 6, 
        message: 'Parsing CSV data...',
        details: 'Analyzing CSV structure and validating format'
      });

      // Step 1: Capture pre-import snapshot
      const preSnapshot = await importVerificationService.capturePreImportSnapshot(importType);
      
      setProgress({ 
        phase: 'processing', 
        currentStep: 3, 
        totalSteps: 6, 
        message: 'Analyzing CSV data...',
        details: 'Processing CSV content and validating data structure'
      });

      // Step 2: Analyze CSV data  
      const csvData = parseCSVContent(csvText);
      const csvAnalysis = await importVerificationService.analyzeCsvData(importType, csvData);
      
      setProgress({ 
        phase: 'processing', 
        currentStep: 4, 
        totalSteps: 6, 
        message: 'Matching drivers...',
        details: `Processing ${csvData.length} records and matching with existing drivers`
      });

      // Step 3: Perform actual import with progress updates
      let processResults;
      
      switch (importType) {
        case 'careem':
          processResults = await importCareemPaymentData(csvText);
          break;
        case 'uber':
          processResults = await importUberCaptainData(csvText);
          break;
        case 'fines':
          processResults = await importTrafficFinesData(csvText);
          break;
        case 'salik':
          processResults = await processSalikCSV(csvText);
          break;
        default:
          throw new Error('Unknown import type');
      }

      if (!processResults.success) {
        throw new Error(processResults.message || 'Import failed');
      }

      setProgress({ 
        phase: 'processing', 
        currentStep: 5, 
        totalSteps: 6, 
        message: 'Recalculating balances...',
        details: 'Updating driver balances and accounting records'
      });

      // Step 4: Recalculate balances
      await recalculateAllDriverBalances();
      
      setProgress({ 
        phase: 'processing', 
        currentStep: 6, 
        totalSteps: 6, 
        message: 'Performing verification...',
        details: 'Capturing post-import data and verifying results'
      });

      // Step 5: Capture post-import snapshot
      const postSnapshot = await importVerificationService.capturePostImportSnapshot(importType);
      
      // Step 6: Perform verification
      const verificationResult = await importVerificationService.performVerification(
        preSnapshot,
        csvAnalysis,
        postSnapshot,
        importType
      );

      setProgress({ 
        phase: 'complete', 
        currentStep: 6, 
        totalSteps: 6, 
        message: 'Import completed successfully!',
        details: `Processed ${processResults.count} records. Ready for verification.`
      });

      return {
        success: true,
        importHistoryId: processResults.batchId,
        verificationResult: verificationResult,
        message: `Successfully imported ${processResults.count} records`
      };
    } catch (error) {
      console.error('Verified import failed:', error);
      setProgress({
        phase: 'error',
        currentStep: 0,
        totalSteps: 6,
        message: 'Import failed',
        details: error instanceof Error ? error.message : 'An unexpected error occurred'
      });
      return {
        success: false,
        message: error instanceof Error ? error.message : 'Import failed'
      };
    }
  };

  // Helper function to parse CSV content
  const parseCSVContent = (csvContent: string): any[] => {
    const lines = csvContent.split(/\r?\n/);
    const headers = lines[0].split(',').map(h => h.trim().replace(/"/g, ''));
    const data = [];
    
    for (let i = 1; i < lines.length; i++) {
      const line = lines[i].trim();
      if (line) {
        const values = line.split(',').map(v => v.trim().replace(/"/g, ''));
        if (values.length >= headers.length - 1) {
          const row: any = {};
          for (let j = 0; j < headers.length; j++) {
            row[headers[j]] = values[j] || '';
          }
          data.push(row);
        }
      }
    }
    
    return data;
  };

  // Verification modal handlers
  const handleVerificationAccept = async () => {
    try {
      if (importHistoryId) {
        await importVerificationService.markImportAsAccepted(importHistoryId);
        setVerificationModalVisible(false);
        
        // Reset progress and state
        setProgress({
          phase: 'idle',
          currentStep: 0,
          totalSteps: 6,
          message: 'Ready to import data',
        });
        setProcessing(false);
        
        // Show success message
        Alert.alert(
          'Import Verified',
          'The import has been accepted and verified successfully.',
          [{ text: 'OK', onPress: () => navigation.goBack() }]
        );
      }
    } catch (error) {
      Alert.alert('Error', 'Failed to accept import verification');
    }
  };

  const handleVerificationReject = async () => {
    try {
      if (importHistoryId) {
        await importVerificationService.markImportAsRejected(importHistoryId);
        setVerificationModalVisible(false);
        
        // Reset progress and state
        setProgress({
          phase: 'idle',
          currentStep: 0,
          totalSteps: 6,
          message: 'Ready to import data',
        });
        setProcessing(false);
        
        Alert.alert(
          'Import Rejected',
          'The import has been rejected. Please review the data and try again.',
          [{ text: 'OK' }]
        );
      }
    } catch (error) {
      Alert.alert('Error', 'Failed to reject import verification');
    }
  };

  // Progress tracking helper functions
  const getStepStatus = (stepNumber: number) => {
    if (progress.currentStep > stepNumber) return 'complete';
    if (progress.currentStep === stepNumber) return 'active';
    return 'pending';
  };

  const getStepColor = (status: string) => {
    switch (status) {
      case 'complete': return theme.colors.success;
      case 'active': return theme.colors.primary;
      default: return theme.colors.gray400;
    }
  };

  // Step indicator component
  const StepIndicator = ({ step, status }: { step: any; status: string }) => (
    <View style={styles.stepItem}>
      <View style={[styles.stepCircle, { borderColor: getStepColor(status) }]}>
        {status === 'complete' ? (
          <Ionicons name="checkmark" size={16} color={theme.colors.success} />
        ) : status === 'active' ? (
          <ActivityIndicator size="small" color={theme.colors.primary} />
        ) : (
          <Text style={[styles.stepNumber, { color: getStepColor(status) }]}>{step.id}</Text>
        )}
      </View>
      <View style={styles.stepInfo}>
        <Text style={[styles.stepName, { color: getStepColor(status) }]}>{step.name}</Text>
        <Text style={styles.stepDescription}>{step.description}</Text>
      </View>
    </View>
  );

  const importTypes = {
    careem: {
      label: 'Careem Captains',
      icon: 'people',
      color: theme.colors.success,
      description: 'Upload Careem captain export CSV (English headers)',
      expectedColumns: ['Captain Id', 'Captain Name', 'Total Trips', 'Total Earnings', 'Cash Balance'],
      sampleFormat: 'Captain Id,Captain Name,Phone Number,Vehicle Type,Available Hours,Acceptance Rate,Total Trips,Total Earnings,Cash Balance',
      fileName: 'partner-portal-captains-export_1752118661021_start_2025-07-06_end_2025-07-12.csv'
    },
    uber: {
      label: 'Uber Drivers',
      icon: 'car',
      color: theme.colors.info,
      description: 'Upload Uber driver payment CSV (Arabic headers)',
      expectedColumns: ['المُعرِّف الفريد العالمي (UUID) للسائق', 'الاسم الأول للشريك السائق', 'إجمالي الدخل'],
      sampleFormat: 'Driver UUID,First Name,Last Name,Total Income,Refunds & Expenses,Cash Collected...',
      fileName: '20250701-20250710-payments_driver-Sharjah_Transport_Solutions.csv'
    },
    fines: {
      label: 'Traffic Fines',
      icon: 'warning',
      color: theme.colors.warning,
      description: 'Upload traffic fines CSV',
      expectedColumns: ['Fine No.', 'Date', 'Plate No.', 'Total Amount', 'Amount after Discount'],
      sampleFormat: 'Fine No.,Date,Fine Place,Plate No.,Total Amount,Discount (%),Amount after Discount',
      fileName: 'traffic fines - Sheet2.csv'
    },
    salik: {
      label: 'Salik Tolls',
      icon: 'card',
      color: theme.colors.primary,
      description: 'Upload Salik toll transactions CSV',
      expectedColumns: ['Plate Number', 'Location', 'Amount', 'Expense Date'],
      sampleFormat: 'Transaction Date,Plate Number,Location,Amount,Expense Date...',
    }
  };

  const parseCSV = (csvText: string): any[] => {
    const lines = csvText.trim().split('\n');
    const headers = lines[0].split(',').map(h => h.trim().replace(/"/g, ''));
    
    return lines.slice(1).map(line => {
      const values = line.split(',').map(v => v.trim().replace(/"/g, ''));
      const row: any = {};
      headers.forEach((header, index) => {
        row[header] = values[index] || '';
      });
      return row;
    });
  };

  // Calculate SHA-256 hash of file content for duplicate detection
  const calculateFileHash = async (fileContent: string): Promise<string> => {
    try {
      if (Platform.OS === 'web') {
        // Use Web Crypto API for browsers
        const encoder = new TextEncoder();
        const data = encoder.encode(fileContent);
        const hashBuffer = await crypto.subtle.digest('SHA-256', data);
        const hashArray = Array.from(new Uint8Array(hashBuffer));
        return hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
      } else {
        // For mobile platforms, use a simple hash function
        let hash = 0;
        for (let i = 0; i < fileContent.length; i++) {
          const char = fileContent.charCodeAt(i);
          hash = ((hash << 5) - hash) + char;
          hash = hash & hash; // Convert to 32-bit integer
        }
        return Math.abs(hash).toString(16);
      }
    } catch (error) {
      console.warn('Hash calculation failed, using timestamp fallback:', error);
      return Date.now().toString(16);
    }
  };

  // Extract date range from filename or use default
  const extractDateRangeFromFilename = (filename: string) => {
    // Try to extract dates from common filename patterns
    // e.g., "careem_2024-07-01_to_2024-07-07.csv"
    const datePattern = /(\d{4}-\d{2}-\d{2})/g;
    const dates = filename.match(datePattern);
    
    if (dates && dates.length >= 2) {
      return {
        startDate: dates[0],
        endDate: dates[dates.length - 1]
      };
    }
    
    // Try week patterns like "week_28_2024"
    const weekPattern = /week[_\s](\d+)[_\s](\d{4})/i;
    const weekMatch = filename.match(weekPattern);
    
    if (weekMatch) {
      const week = parseInt(weekMatch[1]);
      const year = parseInt(weekMatch[2]);
      const startDate = getDateFromWeek(week, year);
      const endDate = new Date(startDate);
      endDate.setDate(startDate.getDate() + 6);
      
      return {
        startDate: startDate.toISOString().split('T')[0],
        endDate: endDate.toISOString().split('T')[0]
      };
    }
    
    // Default to current week if no pattern found
    const today = new Date();
    const startOfWeek = new Date(today.setDate(today.getDate() - today.getDay()));
    const endOfWeek = new Date(startOfWeek);
    endOfWeek.setDate(startOfWeek.getDate() + 6);
    
    return {
      startDate: startOfWeek.toISOString().split('T')[0],
      endDate: endOfWeek.toISOString().split('T')[0]
    };
  };

  // Helper function to get date from week number
  const getDateFromWeek = (week: number, year: number) => {
    const firstDayOfYear = new Date(year, 0, 1);
    const daysOffset = (week - 1) * 7;
    const firstDayOfWeek = firstDayOfYear.getDay();
    const daysToAdd = daysOffset - firstDayOfWeek + 1;
    
    const result = new Date(year, 0, 1 + daysToAdd);
    return result;
  };

  // Enhanced duplicate checking function with business logic
  const checkForDuplicates = async (csvText: string, fileName: string) => {
    try {
      const lines = csvText.split(/\r?\n/);
      const fileHash = await calculateFileHash(csvText);
      const dateRange = extractDateRangeFromFilename(fileName);
      
      // Level 1: Check for exact file duplicate using hash (for Careem/Uber only)
      if (activeImportType === 'careem' || activeImportType === 'uber') {
        const duplicateCheckFunction = activeImportType === 'careem' ? 'check_careem_file_duplicate' : 'check_uber_file_duplicate';
        
        try {
          const { data: fileCheck, error: fileError } = await supabase
            .rpc(duplicateCheckFunction, {
              p_file_hash: fileHash,
              p_start_date: dateRange.startDate,
              p_end_date: dateRange.endDate
            });

          if (!fileError && fileCheck && fileCheck.length > 0 && fileCheck[0].is_duplicate) {
            return {
              hasDuplicates: true,
              duplicateCount: fileCheck[0].record_count,
              isDuplicateFile: true,
              duplicateFileInfo: {
                existingFile: fileCheck[0].existing_file,
                importDate: fileCheck[0].import_date,
                batchId: fileCheck[0].existing_batch_id
              },
              fileHash,
              dateRange
            };
          }
        } catch (dbError) {
          console.log(`Database duplicate check function ${duplicateCheckFunction} not available, proceeding with basic checks`);
        }
      }

      // Level 2: Business logic-based duplicate checking for Salik and Fines
      if (activeImportType === 'salik') {
        return await checkSalikDuplicates(lines, dateRange);
      } else if (activeImportType === 'fines') {
        return await checkFinesDuplicates(lines, dateRange);
      }

      // Level 3: Basic amount-based duplicate checking (fallback for Careem/Uber)
      const { data: existingTransactions, error } = await supabase
        .from('accounting_transactions')
        .select('transaction_date, amount, driver_id')
        .eq('category', getTransactionCategory())
        .gte('transaction_date', dateRange.startDate)
        .lte('transaction_date', dateRange.endDate)
        .limit(1000);

      if (error) {
        console.error('Error fetching existing transactions:', error);
        return { hasDuplicates: false, duplicateCount: 0, fileHash, dateRange };
      }

      let duplicateCount = 0;
      const existingDateAmounts = new Set();
      
      if (existingTransactions && existingTransactions.length > 0) {
        existingTransactions.forEach(tx => {
          existingDateAmounts.add(`${tx.transaction_date}-${tx.amount}`);
        });
      }

      // Check first 100 records for amount-based duplicates
      const recordsToCheck = Math.min(lines.length - 1, 100);
      
      for (let i = 1; i <= recordsToCheck; i++) {
        const line = lines[i]?.trim();
        if (!line) continue;

        const cells = line.split(',');
        let amount = 0;
        
        // Extract amount based on import type
        if (activeImportType === 'careem') {
          amount = parseFloat(cells[7] || '0'); // Total Earnings
        } else if (activeImportType === 'uber') {
          amount = parseFloat(cells[3] || '0'); // Total Income
        }
        
        if (amount > 0) {
          const transactionDate = dateRange.startDate; // Simplified for now
          const checkKey = `${transactionDate}-${amount}`;
          
          if (existingDateAmounts.has(checkKey)) {
            duplicateCount++;
          }
        }
      }

      return {
        hasDuplicates: duplicateCount > 0,
        duplicateCount,
        isDateRangeOverlap: false,
        fileHash,
        dateRange
      };
    } catch (error) {
      console.error('Error checking duplicates:', error);
      return { hasDuplicates: false, duplicateCount: 0 };
    }
  };

  // Smart Salik duplicate checking based on unique Remark field
  const checkSalikDuplicates = async (lines: string[], dateRange: any) => {
    try {
      // Get existing Salik transactions with their descriptions (which contain the Remark)
      const { data: existingSalik, error } = await supabase
        .from('accounting_transactions')
        .select('description')
        .eq('category', 'salik')
        .gte('transaction_date', dateRange.startDate)
        .lte('transaction_date', dateRange.endDate);

      if (error) {
        console.error('Error fetching existing Salik transactions:', error);
        return { hasDuplicates: false, duplicateCount: 0 };
      }

      // Extract unique Remark values from existing transactions
      const existingRemarks = new Set();
      if (existingSalik) {
        existingSalik.forEach(tx => {
          // Extract remark from description (assuming it's stored there)
          // Description format might be: "Salik toll - Remark: 60236982315"
          const remarkMatch = tx.description?.match(/Remark:\s*(\w+)/);
          if (remarkMatch) {
            existingRemarks.add(remarkMatch[1]);
          }
        });
      }

      // Check incoming Salik data for duplicate Remarks
      let duplicateCount = 0;
      const recordsToCheck = Math.min(lines.length - 1, 100);

      for (let i = 1; i <= recordsToCheck; i++) {
        const line = lines[i]?.trim();
        if (!line) continue;

        const cells = line.split(',');
        const remark = cells[17]?.trim() || ''; // Remark column (index 17 based on CSV structure)

        if (remark && existingRemarks.has(remark)) {
          duplicateCount++;
        }
      }

      return {
        hasDuplicates: duplicateCount > 0,
        duplicateCount,
        isBusinessLogicDuplicate: true,
        duplicateType: 'Salik transactions with same Remark numbers (unique identifiers)'
      };
    } catch (error) {
      console.error('Error checking Salik duplicates:', error);
      return { hasDuplicates: false, duplicateCount: 0 };
    }
  };

  // Smart Fines duplicate checking based on unique Fine No. field
  const checkFinesDuplicates = async (lines: string[], dateRange: any) => {
    try {
      // Get existing Fine transactions with their descriptions (which contain the Fine No.)
      const { data: existingFines, error } = await supabase
        .from('accounting_transactions')
        .select('description')
        .eq('category', 'traffic_fine')
        .gte('transaction_date', dateRange.startDate)
        .lte('transaction_date', dateRange.endDate);

      if (error) {
        console.error('Error fetching existing Fine transactions:', error);
        return { hasDuplicates: false, duplicateCount: 0 };
      }

      // Extract unique Fine Numbers from existing transactions
      const existingFineNumbers = new Set();
      if (existingFines) {
        existingFines.forEach(tx => {
          // Extract fine number from description
          // Description format might be: "Traffic Fine No: 618069"
          const fineMatch = tx.description?.match(/Fine No[.:]?\s*(\w+)/i);
          if (fineMatch) {
            existingFineNumbers.add(fineMatch[1]);
          }
        });
      }

      // Check incoming Fine data for duplicate Fine Numbers
      let duplicateCount = 0;
      const recordsToCheck = Math.min(lines.length - 1, 100);

      for (let i = 1; i <= recordsToCheck; i++) {
        const line = lines[i]?.trim();
        if (!line) continue;

        const cells = line.split(',');
        const fineNumber = cells[0]?.trim() || ''; // Fine No. column (index 0)

        if (fineNumber && existingFineNumbers.has(fineNumber)) {
          duplicateCount++;
        }
      }

      return {
        hasDuplicates: duplicateCount > 0,
        duplicateCount,
        isBusinessLogicDuplicate: true,
        duplicateType: 'Traffic fines with same Fine Numbers (unique identifiers)'
      };
    } catch (error) {
      console.error('Error checking Fine duplicates:', error);
      return { hasDuplicates: false, duplicateCount: 0 };
    }
  };

  // Helper function to get the correct transaction category
  const getTransactionCategory = () => {
    switch (activeImportType) {
      case 'careem':
        return 'careem';
      case 'uber':
        return 'uber';
      case 'fines':
        return 'traffic_fine';
      case 'salik':
        return 'salik';
      default:
        return activeImportType;
    }
  };

  const handleFileUpload = async (skipDuplicateCheck = false) => {
    try {
      setProcessing(true);
      setProgress({ 
        phase: 'uploading', 
        currentStep: 1, 
        totalSteps: 6, 
        message: 'Selecting file...',
        details: 'Opening file picker'
      });
      
      setResults({
        processed: 0,
        success: 0,
        failed: 0,
        errors: []
      });
      
      // Use document picker for file selection
      const result = await DocumentPicker.getDocumentAsync({
        type: 'text/csv',
        copyToCacheDirectory: true,
      });

      if (result.canceled) {
        setProcessing(false);
        setProgress({
          phase: 'idle',
          currentStep: 0,
          totalSteps: 6,
          message: 'Ready to import data',
        });
        return;
      }

      const file = result.assets[0];
      
      setProgress({ 
        phase: 'uploading', 
        currentStep: 1, 
        totalSteps: 6, 
        message: 'Reading file...',
        details: `Reading ${file.name} (${(file.size / 1024).toFixed(1)} KB)`
      });

      // Read file content
      const response = await fetch(file.uri);
      const csvText = await response.text();

      if (!csvText || csvText.trim().length === 0) {
        throw new Error('CSV file is empty or invalid');
      }

      // Store filename for verification modal
      setCurrentFileName(file.name || 'imported_file.csv');

      // Enhanced duplicate checking (unless skipped)
      if (!skipDuplicateCheck) {
        setProgress({ 
          phase: 'processing', 
          currentStep: 2, 
          totalSteps: 6, 
          message: 'Checking for duplicates...',
          details: 'Analyzing file hash and checking existing data'
        });

        const duplicateCheck = await Promise.race([
          checkForDuplicates(csvText, file.name || 'imported_file.csv'),
          new Promise(resolve => setTimeout(() => resolve({ hasDuplicates: false, duplicateCount: 0, timeout: true }), 10000))
        ]) as any;
        
        if (duplicateCheck.timeout) {
          setProcessing(false);
          setProgress({
            phase: 'error',
            currentStep: 2,
            totalSteps: 6,
            message: 'Duplicate check timeout',
            details: 'Duplicate checking took too long to complete'
          });
          
          Alert.alert(
            'Duplicate Check Timeout',
            'Duplicate checking is taking too long. Would you like to skip duplicate checking and proceed with import?',
            [
              { 
                text: 'Cancel', 
                style: 'cancel',
                onPress: () => {
                  setProgress({
                    phase: 'idle',
                    currentStep: 0,
                    totalSteps: 6,
                    message: 'Ready to import data',
                  });
                }
              },
              { text: 'Skip & Continue', onPress: () => handleFileUpload(true) }
            ]
          );
          return;
        }
        
        if (duplicateCheck.hasDuplicates) {
          setProcessing(false);
          setProgress({
            phase: 'idle',
            currentStep: 0,
            totalSteps: 6,
            message: 'Duplicate data detected',
            details: `Found ${duplicateCheck.duplicateCount} potential duplicates`
          });
          
          if (duplicateCheck.isDuplicateFile) {
            Alert.alert(
              'Exact File Duplicate Detected!',
              `This exact file was already imported on ${new Date(duplicateCheck.duplicateFileInfo.importDate).toLocaleDateString()}\n\nFile: ${duplicateCheck.duplicateFileInfo.existingFile}\nRecords: ${duplicateCheck.duplicateCount}\nBatch ID: ${duplicateCheck.duplicateFileInfo.batchId.substring(0, 8)}`,
              [
                { 
                  text: 'Cancel', 
                  style: 'cancel',
                  onPress: () => {
                    setProgress({
                      phase: 'idle',
                      currentStep: 0,
                      totalSteps: 6,
                      message: 'Ready to import data',
                    });
                  }
                },
                { text: 'Force Re-import', onPress: () => handleFileUpload(true) }
              ]
            );
          } else if (duplicateCheck.isBusinessLogicDuplicate) {
            Alert.alert(
              'Business Logic Duplicates Detected!',
              `Found ${duplicateCheck.duplicateCount} duplicate ${duplicateCheck.duplicateType || 'records'}.\n\nThese appear to be exact matches based on business rules (same ${activeImportType === 'salik' ? 'vehicle, location, amount and date' : 'fine number, vehicle and amount'}).\n\nContinue with import anyway?`,
              [
                { 
                  text: 'Cancel', 
                  style: 'cancel',
                  onPress: () => {
                    setProgress({
                      phase: 'idle',
                      currentStep: 0,
                      totalSteps: 6,
                      message: 'Ready to import data',
                    });
                  }
                },
                { text: 'Import Anyway', onPress: () => handleFileUpload(true) }
              ]
            );
          } else {
            Alert.alert(
              'Duplicate Data Detected',
              `Found ${duplicateCheck.duplicateCount} potential duplicate records. Continue with import?`,
              [
                { 
                  text: 'Cancel', 
                  style: 'cancel',
                  onPress: () => {
                    setProgress({
                      phase: 'idle',
                      currentStep: 0,
                      totalSteps: 6,
                      message: 'Ready to import data',
                    });
                  }
                },
                { text: 'Continue', onPress: () => handleFileUpload(true) }
              ]
            );
          }
          return;
        }
      } else {
        setProgress({ 
          phase: 'processing', 
          currentStep: 2, 
          totalSteps: 6, 
          message: 'Skipping duplicate check...',
          details: 'Proceeding directly to import'
        });
      }

      // Enhanced import with verification
      const importResult = await performVerifiedImport(activeImportType, csvText, file.name || 'imported_file.csv');
      
      if (importResult.success) {
        setImportHistoryId(importResult.importHistoryId);
        setVerificationResult(importResult.verificationResult);
        setVerificationModalVisible(true);
      } else {
        throw new Error(importResult.message);
      }
    } catch (error) {
      setProcessing(false);
      console.error('Import error:', error);
      setProgress({
        phase: 'error',
        currentStep: 0,
        totalSteps: 6,
        message: 'Import failed',
        details: error instanceof Error ? error.message : 'An unexpected error occurred'
      });
      
      Alert.alert(
        'Import Error',
        error instanceof Error ? error.message : 'An error occurred during import'
      );
    }
  };

  const currentType = importTypes[activeImportType];

  return (
    <>
      <ScrollView style={styles.container}>
      {/* Header */}
      <View style={styles.header}>
        <TouchableOpacity onPress={() => navigation.goBack()} style={styles.backButton}>
          <Ionicons name="arrow-back" size={24} color={theme.colors.primary} />
        </TouchableOpacity>
        <Text style={styles.headerTitle}>CSV Import</Text>
      </View>

      {/* Import Type Selection */}
      <View style={styles.section}>
        <Text style={styles.sectionTitle}>Select Import Type</Text>
        <View style={styles.typeGrid}>
          {Object.entries(importTypes).map(([key, type]) => (
            <TouchableOpacity
              key={key}
              style={[
                styles.typeCard,
                activeImportType === key && { borderColor: type.color, borderWidth: 2 }
              ]}
              onPress={() => setActiveImportType(key as any)}
            >
              <View style={[styles.typeIcon, { backgroundColor: type.color + '20' }]}>
                <Ionicons name={type.icon as any} size={24} color={type.color} />
              </View>
              <Text style={styles.typeLabel}>{type.label}</Text>
            </TouchableOpacity>
          ))}
        </View>
      </View>

      {/* Import Instructions */}
      <View style={[styles.section, styles.instructionsSection]}>
        <View style={[styles.instructionsHeader, { backgroundColor: currentType.color + '10' }]}>
          <Ionicons name={currentType.icon as any} size={20} color={currentType.color} />
          <Text style={[styles.instructionsTitle, { color: currentType.color }]}>
            {currentType.label} Import
          </Text>
        </View>
        
        <Text style={styles.instructionsDescription}>
          {currentType.description}
        </Text>

        <View style={styles.columnsSection}>
          <Text style={styles.columnsTitle}>Expected columns:</Text>
          <View style={styles.columnsList}>
            {currentType.expectedColumns.map(col => (
              <View key={col} style={[styles.columnTag, { backgroundColor: currentType.color + '20' }]}>
                <Text style={[styles.columnText, { color: currentType.color }]}>{col}</Text>
              </View>
            ))}
          </View>
        </View>

        <View style={styles.sampleSection}>
          <Text style={styles.sampleTitle}>Sample format:</Text>
          <View style={styles.sampleBox}>
            <Text style={styles.sampleText}>{currentType.sampleFormat}</Text>
          </View>
        </View>
      </View>

      {/* Import Progress Section */}
      <View style={styles.section}>
        <Text style={styles.sectionTitle}>Import Progress</Text>
        <View style={styles.stepsContainer}>
          {steps.map((step) => (
            <StepIndicator 
              key={step.id} 
              step={step} 
              status={getStepStatus(step.id)} 
            />
          ))}
        </View>

        {progress.phase !== 'idle' && (
          <View style={[styles.progressCard, { borderLeftColor: theme.colors.primary }]}>
            <View style={styles.progressHeader}>
              <Text style={styles.progressMessage}>{progress.message}</Text>
              {progress.phase === 'processing' && (
                <ActivityIndicator size="small" color={theme.colors.primary} />
              )}
            </View>
            {progress.details && (
              <Text style={styles.progressDetails}>{progress.details}</Text>
            )}
            {progress.processed && progress.total && (
              <View style={styles.progressBar}>
                <View 
                  style={[
                    styles.progressFill, 
                    { 
                      width: `${(progress.processed / progress.total) * 100}%`,
                      backgroundColor: theme.colors.primary
                    }
                  ]} 
                />
              </View>
            )}
          </View>
        )}
      </View>

      {/* File Upload Section */}
      <View style={styles.section}>
        <TouchableOpacity 
          style={[styles.uploadButton, { backgroundColor: currentType.color }]}
          onPress={() => handleFileUpload(false)}
          disabled={processing}
        >
          {processing ? (
            <ActivityIndicator color={theme.colors.white} />
          ) : (
            <Ionicons name="cloud-upload" size={24} color={theme.colors.white} />
          )}
          <Text style={styles.uploadButtonText}>
            {processing ? 'Processing...' : 'Choose CSV File (with Duplicate Check)'}
          </Text>
        </TouchableOpacity>
        
        <TouchableOpacity 
          style={[styles.uploadButton, { backgroundColor: '#FF8800', marginTop: 8 }]}
          onPress={() => handleFileUpload(true)}
          disabled={processing}
        >
          {processing ? (
            <ActivityIndicator color={theme.colors.white} />
          ) : (
            <Ionicons name="flash" size={24} color={theme.colors.white} />
          )}
          <Text style={styles.uploadButtonText}>
            {processing ? 'Processing...' : 'Quick Import (Skip Duplicate Check)'}
          </Text>
        </TouchableOpacity>
      </View>

      {/* Results Section */}
      {results && (
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Import Results</Text>
          
          <View style={styles.resultsGrid}>
            <View style={styles.resultCard}>
              <Text style={styles.resultNumber}>{results.processed}</Text>
              <Text style={styles.resultLabel}>Total Processed</Text>
            </View>
            
            <View style={[styles.resultCard, { borderLeftColor: theme.colors.success }]}>
              <Text style={[styles.resultNumber, { color: theme.colors.success }]}>{results.success}</Text>
              <Text style={styles.resultLabel}>Successful</Text>
            </View>
            
            <View style={[styles.resultCard, { borderLeftColor: theme.colors.error }]}>
              <Text style={[styles.resultNumber, { color: theme.colors.error }]}>{results.failed}</Text>
              <Text style={styles.resultLabel}>Failed</Text>
            </View>
          </View>

          {results.errors && results.errors.length > 0 && (
            <View style={styles.errorsSection}>
              <Text style={styles.errorsTitle}>Errors ({results.errors.length}):</Text>
              <View style={styles.errorsList}>
                {results.errors.slice(0, 5).map((error: string, index: number) => (
                  <Text key={index} style={styles.errorText}>• {error}</Text>
                ))}
                {results.errors.length > 5 && (
                  <Text style={styles.errorText}>... and {results.errors.length - 5} more errors</Text>
                )}
              </View>
            </View>
          )}
        </View>
      )}

      {/* ID Matching Information */}
      <View style={styles.section}>
        <Text style={styles.sectionTitle}>Driver ID Matching System</Text>
        <View style={styles.matchingGrid}>
          <View style={styles.matchingCard}>
            <View style={styles.matchingHeader}>
              <Ionicons name="people" size={16} color={theme.colors.success} />
              <Text style={styles.matchingTitle}>Careem</Text>
            </View>
            <Text style={styles.matchingDescription}>
              Matches using captain_id field from your driver records
            </Text>
          </View>
          
          <View style={styles.matchingCard}>
            <View style={styles.matchingHeader}>
              <Ionicons name="car" size={16} color={theme.colors.info} />
              <Text style={styles.matchingTitle}>Uber</Text>
            </View>
            <Text style={styles.matchingDescription}>
              Matches using driver_uuid field from your driver records
            </Text>
          </View>
          
          <View style={styles.matchingCard}>
            <View style={styles.matchingHeader}>
              <Ionicons name="warning" size={16} color={theme.colors.warning} />
              <Text style={styles.matchingTitle}>Fines & Tolls</Text>
            </View>
            <Text style={styles.matchingDescription}>
              Matches using license_plate number to assign to current driver
            </Text>
          </View>
        </View>
      </View>
    </ScrollView>

    {/* Import Verification Modal */}
    <ImportVerificationModal
      visible={verificationModalVisible}
      verificationResult={verificationResult}
      fileName={currentFileName}
      onAccept={handleVerificationAccept}
      onReject={handleVerificationReject}
      onClose={() => setVerificationModalVisible(false)}
    />
    </>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: theme.colors.white,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: theme.spacing.md,
    paddingVertical: theme.spacing.sm,
    borderBottomWidth: 1,
    borderBottomColor: theme.colors.gray200,
    gap: theme.spacing.sm,
  },
  backButton: {
    padding: theme.spacing.xs,
  },
  headerTitle: {
    fontSize: theme.typography.sizes.lg,
    fontWeight: '600' as any,
    color: theme.colors.textPrimary,
  },
  section: {
    padding: theme.spacing.md,
  },
  sectionTitle: {
    fontSize: theme.typography.sizes.lg,
    fontWeight: '600' as any,
    color: theme.colors.textPrimary,
    marginBottom: theme.spacing.md,
  },
  typeGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: theme.spacing.sm,
  },
  typeCard: {
    backgroundColor: theme.colors.gray50,
    borderRadius: theme.borderRadius.md,
    padding: theme.spacing.md,
    alignItems: 'center',
    minWidth: '45%',
    borderWidth: 1,
    borderColor: theme.colors.gray200,
  },
  typeIcon: {
    width: 48,
    height: 48,
    borderRadius: 24,
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: theme.spacing.sm,
  },
  typeLabel: {
    fontSize: theme.typography.sizes.sm,
    fontWeight: '500' as any,
    color: theme.colors.textPrimary,
    textAlign: 'center',
  },
  instructionsSection: {
    backgroundColor: theme.colors.gray50,
  },
  instructionsHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: theme.spacing.sm,
    padding: theme.spacing.sm,
    borderRadius: theme.borderRadius.sm,
    marginBottom: theme.spacing.md,
  },
  instructionsTitle: {
    fontSize: theme.typography.sizes.md,
    fontWeight: '600' as any,
  },
  instructionsDescription: {
    fontSize: theme.typography.sizes.md,
    color: theme.colors.gray600,
    marginBottom: theme.spacing.md,
    lineHeight: 20,
  },
  columnsSection: {
    marginBottom: theme.spacing.md,
  },
  columnsTitle: {
    fontSize: theme.typography.sizes.sm,
    fontWeight: '500' as any,
    color: theme.colors.textPrimary,
    marginBottom: theme.spacing.sm,
  },
  columnsList: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: theme.spacing.xs,
  },
  columnTag: {
    paddingHorizontal: theme.spacing.sm,
    paddingVertical: 4,
    borderRadius: 12,
  },
  columnText: {
    fontSize: theme.typography.sizes.xs,
    fontWeight: '500' as any,
  },
  sampleSection: {
    marginBottom: theme.spacing.md,
  },
  sampleTitle: {
    fontSize: theme.typography.sizes.sm,
    fontWeight: '500' as any,
    color: theme.colors.textPrimary,
    marginBottom: theme.spacing.sm,
  },
  sampleBox: {
    backgroundColor: theme.colors.white,
    borderRadius: theme.borderRadius.sm,
    padding: theme.spacing.sm,
    borderWidth: 1,
    borderColor: theme.colors.gray200,
  },
  sampleText: {
    fontSize: theme.typography.sizes.xs,
    color: theme.colors.gray600,
    fontFamily: Platform.OS === 'ios' ? 'Courier' : 'monospace',
  },
  // Progress tracking styles
  stepsContainer: {
    gap: theme.spacing.md,
    marginBottom: theme.spacing.lg,
  },
  stepItem: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  stepCircle: {
    width: 32,
    height: 32,
    borderRadius: 16,
    borderWidth: 2,
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: theme.spacing.md,
  },
  stepNumber: {
    fontSize: theme.typography.sizes.sm,
    fontWeight: '600' as any,
  },
  stepInfo: {
    flex: 1,
  },
  stepName: {
    fontSize: theme.typography.sizes.md,
    fontWeight: '500' as any,
    marginBottom: 2,
  },
  stepDescription: {
    fontSize: theme.typography.sizes.sm,
    color: theme.colors.textSecondary,
  },
  progressCard: {
    padding: theme.spacing.md,
    backgroundColor: theme.colors.gray50,
    borderRadius: theme.borderRadius.lg,
    borderLeftWidth: 4,
  },
  progressHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: theme.spacing.xs,
  },
  progressMessage: {
    fontSize: theme.typography.sizes.md,
    fontWeight: '500' as any,
    color: theme.colors.textPrimary,
  },
  progressDetails: {
    fontSize: theme.typography.sizes.sm,
    color: theme.colors.textSecondary,
    marginBottom: theme.spacing.sm,
  },
  progressBar: {
    height: 4,
    backgroundColor: theme.colors.gray200,
    borderRadius: 2,
    overflow: 'hidden',
  },
  progressFill: {
    height: '100%',
    backgroundColor: theme.colors.primary,
  },
  uploadButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: theme.spacing.md,
    paddingHorizontal: theme.spacing.lg,
    borderRadius: theme.borderRadius.md,
    gap: theme.spacing.sm,
  },
  uploadButtonText: {
    color: theme.colors.white,
    fontSize: theme.typography.sizes.md,
    fontWeight: '600' as any,
  },
  resultsGrid: {
    flexDirection: 'row',
    gap: theme.spacing.sm,
    marginBottom: theme.spacing.md,
  },
  resultCard: {
    flex: 1,
    backgroundColor: theme.colors.white,
    borderRadius: theme.borderRadius.md,
    padding: theme.spacing.md,
    alignItems: 'center',
    borderLeftWidth: 4,
    borderLeftColor: theme.colors.gray300,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.1,
    shadowRadius: 2,
    elevation: 2,
  },
  resultNumber: {
    fontSize: theme.typography.sizes.xl,
    fontWeight: '700' as any,
    color: theme.colors.textPrimary,
  },
  resultLabel: {
    fontSize: theme.typography.sizes.sm,
    color: theme.colors.gray600,
    marginTop: 4,
  },
  errorsSection: {
    backgroundColor: theme.colors.error + '10',
    borderRadius: theme.borderRadius.md,
    padding: theme.spacing.md,
    borderWidth: 1,
    borderColor: theme.colors.error + '30',
  },
  errorsTitle: {
    fontSize: theme.typography.sizes.sm,
    fontWeight: '600' as any,
    color: theme.colors.error,
    marginBottom: theme.spacing.sm,
  },
  errorsList: {
    gap: 4,
  },
  errorText: {
    fontSize: theme.typography.sizes.sm,
    color: theme.colors.error,
    lineHeight: 18,
  },
  matchingGrid: {
    gap: theme.spacing.sm,
  },
  matchingCard: {
    backgroundColor: theme.colors.gray50,
    borderRadius: theme.borderRadius.md,
    padding: theme.spacing.md,
    borderWidth: 1,
    borderColor: theme.colors.gray200,
  },
  matchingHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: theme.spacing.sm,
    marginBottom: theme.spacing.sm,
  },
  matchingTitle: {
    fontSize: theme.typography.sizes.sm,
    fontWeight: '600' as any,
    color: theme.colors.textPrimary,
  },
  matchingDescription: {
    fontSize: theme.typography.sizes.sm,
    color: theme.colors.gray600,
    lineHeight: 18,
  },
});
